import {
  ConditionsLogicOperator,
  type ActionConfig,
  type AutomationConfig,
  type ValueConfig,
} from "@smartsuite/automation-engine-api";

import { t } from "i18next";

import * as R from "remeda";
import type {
  ActionGroupCandidate,
  AutomationCandidate,
} from "../../types/candidate";
import { stripEmptyActionsGroups } from "../../stores/automation/utils/action-group-walk";

const mapActionToMutableFields = (
  action: ActionConfig,
): {
  actionReference: ActionConfig["actionReference"];
  conditions: ActionConfig["conditions"];
  inputs: ValueConfig[];
} => ({
  actionReference: action.actionReference
    ? {
        integrationId: action.actionReference.integrationId,
        actionId: action.actionReference.actionId,
        instanceId: action.actionReference.instanceId,
      }
    : {
        integrationId: "",
        actionId: "",
        instanceId: 0,
      },
  conditions: action.conditions,
  inputs: action.inputs
    .filter((i) => R.isTruthy(i.value) && i.value.values.length > 0)
    .map((i) => ({
      ...i.value,
      values:
        i.value?.values.map((v) => ({
          value: v.value,
        })) ?? [],
    })),
});

const mapActionGroupsToMutableFields = (
  actionGroups: ActionGroupCandidate[],
): unknown => {
  return actionGroups.map((actionGroup) => {
    if (actionGroup.config.oneofKind === "actions") {
      return {
        instanceId: actionGroup.instanceId,
        config: {
          oneofKind: actionGroup.config.oneofKind,
          actions: {
            actions: actionGroup.config.actions.actions.map(
              mapActionToMutableFields,
            ),
          },
        },
      };
    } else if (actionGroup.config.oneofKind === "loop") {
      return {
        instanceId: actionGroup.instanceId,
        config: {
          oneofKind: "loop",
          loop: {
            name: actionGroup.config.loop.name,
            source: actionGroup.config.loop.source,
            actionGroups: mapActionGroupsToMutableFields(
              actionGroup.config.loop.actionGroups,
            ),
          },
        },
      };
    } else if (actionGroup.config.oneofKind === "branches") {
      // Branch/elseBranch `hash` is UI-only identity (selection/keys/DnD) --
      // intentionally not projected here, same as ActionCandidate.hash above.
      return {
        instanceId: actionGroup.instanceId,
        config: {
          oneofKind: "branches",
          branches: {
            label: actionGroup.config.branches.label,
            branches: actionGroup.config.branches.branches.map((branch) => ({
              label: branch.label,
              conditions: branch.conditions,
              actionGroups: mapActionGroupsToMutableFields(branch.actionGroups),
            })),
            elseBranch:
              actionGroup.config.branches.elseBranch === undefined
                ? undefined
                : {
                    label: actionGroup.config.branches.elseBranch.label,
                    actionGroups: mapActionGroupsToMutableFields(
                      actionGroup.config.branches.elseBranch.actionGroups,
                    ),
                  },
          },
        },
      };
    }
    return actionGroup;
  });
};

const isNew = (candidate: AutomationCandidate): boolean => {
  return candidate.automationId === undefined;
};

export const hasUnsavedChanges = ({
  labelInput,
  candidate,
  candidateBeforeEdit,
}: {
  labelInput: string;
  candidate: AutomationCandidate;
  candidateBeforeEdit?: AutomationConfig;
}): boolean => {
  // Empty direct "actions" groups are UI scaffolding, not user content:
  // release() strips them from the save payload and cloneAndSetAutomation
  // re-seeds them (with fresh instanceIds) on hydrate, so the live candidate
  // and the raw before-edit config legitimately disagree about them. Strip
  // both sides before projecting so that scaffolding never counts as an
  // unsaved change; once a user adds an action the group is non-empty and
  // participates on both sides consistently.
  const mutableFields = {
    label: labelInput,
    description: candidate.description,
    triggerReference: candidate.trigger?.triggerReference,
    conditions: candidate.trigger?.conditions,
    inputs: candidate.trigger?.inputs,
    actionGroups: mapActionGroupsToMutableFields(
      stripEmptyActionsGroups(candidate.actionGroups),
    ),
  };
  if (isNew(candidate)) {
    const emptyCandidate = {
      label: "New Automation",
      description: "",
      triggerReference: {
        integrationId: "",
        triggerId: "",
      },
      conditions: {
        operator: ConditionsLogicOperator.OPERATOR_AND,
        group: [],
      },
      inputs: [],
      actions: [],
    };
    return !R.equals(mutableFields, emptyCandidate);
  }

  if (candidateBeforeEdit === undefined) return false;

  const mutableFieldsBeforeEdit = {
    label: candidateBeforeEdit.label,
    description: candidateBeforeEdit.description,
    triggerReference: candidateBeforeEdit?.trigger?.triggerReference,
    conditions: candidateBeforeEdit?.trigger?.conditions,
    inputs: candidateBeforeEdit?.trigger?.inputs,
    actionGroups: mapActionGroupsToMutableFields(
      stripEmptyActionsGroups(
        candidateBeforeEdit.actionGroups as ActionGroupCandidate[],
      ),
    ),
  };

  return !R.equals(mutableFields, mutableFieldsBeforeEdit);
};

export const generateAutomationLabel = (
  sortedExistingLabels: string[],
): string => {
  const defaultLabel = t("automation-form.new-automation");
  const regexString = "^" + defaultLabel + " \\d+$";
  const defaultLabelRegex = new RegExp(regexString);
  let newIndex = 1;
  let found = true;
  while (found) {
    found = false;
    for (let i = 0; i < sortedExistingLabels.length; i++) {
      const label = sortedExistingLabels[i];
      if (defaultLabelRegex.test(label)) {
        const labelIndex = parseInt(label.split(" ")[2], 10);
        if (labelIndex === newIndex) {
          found = true;
          break;
        }
      }
    }
    if (found) {
      newIndex++;
    }
  }
  return `${defaultLabel} ${newIndex}`;
};
